import { z } from 'zod'
import { RubricType } from '@breakoutlearning/firebase-repository/types'
import { type SlideModel } from '@breakoutlearning/firebase-repository/models/SlideModel'
import { type useTranslationTyped } from 'i18n/i18n'

export function isPromptRequired(rubricType: RubricType | undefined): boolean {
  if (rubricType === RubricType.decisionMaking) return false
  if (rubricType === RubricType.evidence) return false
  if (rubricType === RubricType.peerEngagement) return false
  if (rubricType === RubricType.criticalThinking) return false
  // Graded against the per-score criteria; the directive is optional extra.
  if (rubricType === RubricType.learningObjective) return false
  return true
}

/** Scores a learningObjective rubric requires criteria for (fixed 0-4 scale). */
export const LEARNING_OBJECTIVE_SCORES = [0, 1, 2, 3, 4] as const

export function shouldPrefillTitleAndDescription(
  rubricType: RubricType | undefined
): boolean {
  if (rubricType === undefined) return false

  if (rubricType === RubricType.blooms) return false
  if (rubricType === RubricType.passFail) return false
  // The title IS the author's learning objective; the type title would only
  // get in the way.
  if (rubricType === RubricType.learningObjective) return false

  return true
}

export const getRubricSchema = (
  tt: ReturnType<typeof useTranslationTyped>['tt'],
  slides?: SlideModel[],
  // Effective per-type requirement, from the rubric type docs. Falls back to
  // isPromptRequired() for any type not present in the map.
  requiresPromptByType?: Partial<Record<RubricType, boolean>>
) => {
  const obj = {
    rubric: z
      .string({ message: tt.rubric.slide_rubric_invalid() })
      .trim()
      .min(1, { message: tt.rubric.slide_rubric_invalid() }),
    rubricDescription: z
      .string({ message: tt.rubric.slide_rubric_description_invalid() })
      .trim()
      .min(1, { message: tt.rubric.slide_rubric_description_invalid() }),
    rubricPrompt: z.string({
      message: tt.rubric.slide_rubric_prompt_invalid(),
    }),
    rubricType: z.nativeEnum(RubricType),
    rubricLabel0: z.string().optional(),
    rubricLabel1: z.string().optional(),
    slideId: z.string(), // End slide ID (or empty string for 'all slides')
    // New fields for slide selection mode
    slideSelectionMode: z.enum(['all', 'custom']),
    startSlideId: z.string().optional(), // Only used for custom ranges
    assignmentRoles: z.array(z.string()).optional(), // Role assignment for rubric
    variantKey: z.string().optional(), // Set when prefilled from a rubric type variant
    // Per-score criteria (index = score 0-4), required for learningObjective
    rubricScoreCriteria: z.array(z.string()).optional(),
  }

  // Helper function to get slide index by ID
  const getSlideIndex = (slideId: string): number => {
    if (!slides) return -1
    return slides.findIndex((slide) => slide.id === slideId)
  }

  return z.preprocess(
    (data) => {
      if (data === null || data === undefined) return data
      if (typeof data !== 'object') return data

      const safeData = data as {
        rubricPrompt?: string
        rubricType: RubricType
        slideSelectionMode?: 'all' | 'custom'
        startSlideId?: string
        slideId?: string
      }

      const prompt = safeData.rubricPrompt?.trim()
      const promptRequired =
        requiresPromptByType?.[safeData.rubricType] ??
        isPromptRequired(safeData.rubricType)

      // If prompt is required but not provided, return the object with the prompt field undefined
      // This makes the schema validation fail, which in turn will show an error in the form
      if (promptRequired && !prompt) {
        return {
          ...data,
          rubricPrompt: undefined,
        }
      }

      return {
        ...data,
        // nsure rubricPrompt is always a string, even if empty when it's not required
        rubricPrompt: prompt || '',
      }
    },
    z
      .object(obj)
      .refine(
        (data) => {
          // Custom validation for slide selection
          if (data.slideSelectionMode === 'custom') {
            // End slide is required for custom mode, start slide is optional
            if (!data.slideId) return false
          }
          return true
        },
        {
          message: tt.rubric.end_slide_invalid(),
          path: ['slideId'],
        }
      )
      .superRefine((data, ctx) => {
        // learningObjective rubrics must define what earns each score 0-4
        if (data.rubricType !== RubricType.learningObjective) return
        for (const score of LEARNING_OBJECTIVE_SCORES) {
          if (!data.rubricScoreCriteria?.[score]?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tt.rubric.score_criteria_invalid(),
              path: ['rubricScoreCriteria', score],
            })
          }
        }
      })
      .refine(
        (data) => {
          // validate that end slide comes after start slide using slide order (only if start slide is provided)
          if (
            data.slideSelectionMode === 'custom' &&
            data.startSlideId &&
            data.slideId &&
            slides
          ) {
            const startIndex = getSlideIndex(data.startSlideId)
            const endIndex = getSlideIndex(data.slideId)

            // If either slide is not found, fail validation
            if (startIndex === -1 || endIndex === -1) return false

            // Start slide must come before or be the same as end slide
            return startIndex <= endIndex
          }
          return true
        },
        {
          message: tt.rubric.end_slide_must_be_after_start(),
          path: ['slideId'],
        }
      )
  )
}
